/**
 * AppLayout Component - Phase 2C-1
 * 
 * Main application shell that wraps all pages.
 * Includes header, optional sidebar (admin), and main content area.
 */

import React from 'react';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { useAuth } from '../../context/AuthContext';

export interface AppLayoutProps {
  children: React.ReactNode;
}

export function AppLayout({ children }: AppLayoutProps): JSX.Element {
  const { isAdmin } = useAuth();
  
  return (
    <div className="min-h-screen bg-slate-900 text-slate-100">
      {/* Header */}
      <Header />
      
      {/* Main body */}
      <div className="flex">
        {/* Sidebar - only for admins */}
        {isAdmin && <Sidebar />}
        
        {/* Main content */}
        <main
          className={`
            flex-1 min-h-[calc(100vh-64px)]
            ${isAdmin ? 'ml-60' : ''}
          `.trim()}
        >
          {children}
        </main>
      </div>
    </div>
  );
}

/**
 * AppLayoutSimple - Layout without sidebar (for non-admin pages)
 */
export function AppLayoutSimple({ children }: AppLayoutProps): JSX.Element {
  return (
    <div className="min-h-screen bg-slate-900 text-slate-100">
      <Header />
      <main className="min-h-[calc(100vh-64px)]">
        {children}
      </main>
    </div>
  );
}

/**
 * AppLayoutCentered - Centered content layout (for auth pages, etc.)
 */
export function AppLayoutCentered({ children }: AppLayoutProps): JSX.Element {
  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 flex flex-col">
      <Header />
      <main className="flex-1 flex items-center justify-center p-6">
        {children}
      </main>
    </div>
  );
}

export default AppLayout;
